import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../hooks/useAuth';

/**
 * 受保護路由 Props
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * 受保護路由組件
 *
 * 只允許已認證的使用者訪問
 * 未認證使用者將被重定向到登入頁面
 * 認證狀態載入中時顯示載入動畫
 *
 * @example
 * ```tsx
 * <Route path="/dashboard" element={
 *   <ProtectedRoute>
 *     <DashboardPage />
 *   </ProtectedRoute>
 * } />
 * ```
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // 載入中狀態
  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          載入中...
        </Typography>
      </Box>
    );
  }

  // 未認證，重定向到登入頁
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 已認證，顯示子組件
  return <>{children}</>;
};

export default ProtectedRoute;
